/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { ChevronDown, FileText, MoreHorizontal, Plus, Search, Star } from "@/components/icons"
import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { SidebarSearchResultItem } from "@/components/sidebar/sidebar-search-result-item"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useDocuments } from "@/hooks/use-documents"
import { useToggleFavoriteDocument, useUserSettings } from "@/hooks/use-user-settings"
import { cn } from "@/lib/utils"

const DEFAULT_VISIBLE_COUNT = 5
const FAVORITES_SECTION_STORAGE_KEY = "sidebar-documents-favorites-open"

type DocumentsFolderProps = {
  className?: string
  defaultExpanded?: boolean
  globalSearchTerm?: string
  onCreateDocument?: () => void
}

export function DocumentsFolder({
  className,
  defaultExpanded = true,
  globalSearchTerm = "",
  onCreateDocument,
}: DocumentsFolderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile
  const { data: documents, isPending: isLoadingDocuments } = useDocuments()
  const { data: userSettings } = useUserSettings()
  const toggleFavoriteDocument = useToggleFavoriteDocument()

  const [localSearchTerm, setLocalSearchTerm] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)
  const [isCollapsedPopoverOpen, setIsCollapsedPopoverOpen] = useState(false)
  const [isRecentSectionOpen, setIsRecentSectionOpen] = useState(true)
  const [isFavoritesSectionOpenStored, setIsFavoritesSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(FAVORITES_SECTION_STORAGE_KEY)
    return stored !== null ? stored === "true" : true
  })
  const [openMenuDocumentId, setOpenMenuDocumentId] = useState<string | null>(null)

  const effectiveSearchTerm = globalSearchTerm || localSearchTerm
  const isSearchingDocuments = Boolean(effectiveSearchTerm)
  const isFavoritesSectionOpen = isSearchingDocuments ? true : isFavoritesSectionOpenStored

  const setIsFavoritesSectionOpen = useCallback((open: boolean) => {
    setIsFavoritesSectionOpenStored(open)
    if (typeof window !== "undefined") {
      localStorage.setItem(FAVORITES_SECTION_STORAGE_KEY, String(open))
    }
  }, [])

  const filteredDocuments = useMemo(() => {
    const sortedDocuments = [...(documents ?? [])].sort((a, b) => {
      const aTime = new Date(a.updated ?? a.created ?? 0).getTime()
      const bTime = new Date(b.updated ?? b.created ?? 0).getTime()
      return bTime - aTime
    })

    if (!effectiveSearchTerm) {
      return sortedDocuments
    }

    const search = effectiveSearchTerm.toLowerCase()
    return sortedDocuments.filter(
      (document) =>
        document.title.toLowerCase().includes(search) ||
        document.id.toLowerCase().includes(search) ||
        (document.content || "").toLowerCase().includes(search)
    )
  }, [documents, effectiveSearchTerm])

  const favoriteDocumentIds = useMemo(
    () => new Set(userSettings?.data.favorite_documents ?? []),
    [userSettings?.data.favorite_documents]
  )

  const favoriteDocuments = useMemo(
    () => filteredDocuments.filter((document) => favoriteDocumentIds.has(document.id)),
    [favoriteDocumentIds, filteredDocuments]
  )
  const recentDocuments = useMemo(
    () => filteredDocuments.filter((document) => !favoriteDocumentIds.has(document.id)),
    [favoriteDocumentIds, filteredDocuments]
  )
  const visibleFavoriteDocuments = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return favoriteDocuments
    }
    return favoriteDocuments.slice(0, DEFAULT_VISIBLE_COUNT)
  }, [effectiveSearchTerm, favoriteDocuments, showAllItems])
  const visibleRecentDocuments = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return recentDocuments
    }
    const remainingSlots = Math.max(0, DEFAULT_VISIBLE_COUNT - visibleFavoriteDocuments.length)
    return recentDocuments.slice(0, remainingSlots)
  }, [effectiveSearchTerm, recentDocuments, showAllItems, visibleFavoriteDocuments.length])
  const visibleDocumentsCount = visibleFavoriteDocuments.length + visibleRecentDocuments.length
  const sectionVisibleCount = effectiveSearchTerm
    ? filteredDocuments.length
    : Math.min(DEFAULT_VISIBLE_COUNT, filteredDocuments.length)
  const hasMoreItems = filteredDocuments.length > visibleDocumentsCount

  const handleCreateDocument = () => {
    if (onCreateDocument) {
      onCreateDocument()
      return
    }
    void navigate("/documents/new")
  }

  const headerActions = (
    <>
      {!globalSearchTerm && (
        <Button
          aria-label={t("documents.search", { defaultValue: "Search" })}
          size="icon"
          variant="ghost"
          className={cn(
            "hover:bg-foreground/15 size-7 rounded-full p-0",
            localSearchTerm && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={(event) => {
            event.stopPropagation()
            setIsSearchOpen(true)
          }}
        >
          <Search className="size-4" />
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={t("documents.new", { defaultValue: "New document" })}
            className="hover:bg-foreground/15 size-7 rounded-full p-0"
            size="icon"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation()
              handleCreateDocument()
            }}
          >
            <Plus className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={5}>
          <p>{t("documents.new", { defaultValue: "New document" })}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  const renderDocumentItem = (document: (typeof filteredDocuments)[number]) => {
    const isFavorite = favoriteDocumentIds.has(document.id)
    const isActive = location.pathname === `/documents/${document.id}`
    return (
      <div
        key={document.id}
        className="group/document-item relative"
        data-active={isActive ? "true" : undefined}
      >
        <SidebarSearchResultItem
          className="pr-10"
          isActive={isActive}
          timestamp={document.updated ?? document.created}
          title={document.title || t("documents.untitled", { defaultValue: "Untitled document" })}
          onClick={() => {
            void navigate(`/documents/${document.id}`)
            if (isCollapsedSidebar) {
              setIsCollapsedPopoverOpen(false)
            }
          }}
        />
        <DropdownMenu
          open={openMenuDocumentId === document.id}
          onOpenChange={(isOpen) => {
            setOpenMenuDocumentId(isOpen ? document.id : null)
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={t("documents.more_actions", { defaultValue: "More actions" })}
              size="icon"
              type="button"
              variant="ghost"
              className={cn(
                "absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2 rounded-md p-1",
                "hover:bg-sidebar-accent-foreground/15 dark:hover:bg-sidebar-foreground/20 opacity-0 transition-opacity duration-150",
                "dark:text-sidebar-foreground",
                "group-data-[active=true]/document-item:!text-sidebar-accent-foreground",
                "group-hover/document-item:opacity-100",
                openMenuDocumentId === document.id && "opacity-100"
              )}
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              <MoreHorizontal className="size-3.5 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right">
            <DropdownMenuItem
              className="gap-2"
              disabled={toggleFavoriteDocument.isPending}
              onClick={(event) => {
                event.stopPropagation()
                toggleFavoriteDocument.mutate({ documentId: document.id })
              }}
            >
              <Star className="size-4" />
              {isFavorite
                ? t("documents.unfavorite", { defaultValue: "Remove favorite" })
                : t("documents.favorite", { defaultValue: "Mark as favorite" })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  if (isCollapsedSidebar) {
    return (
      <Popover
        open={isCollapsedPopoverOpen}
        onOpenChange={(open) => {
          setIsCollapsedPopoverOpen(open)
          if (!open) {
            setShowAllItems(false)
            setOpenMenuDocumentId(null)
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={t("documents.title", { defaultValue: "Documents" })}
            type="button"
            className={cn(
              "group/nav-section hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex size-10 items-center justify-center rounded-md transition-colors duration-150",
              className
            )}
          >
            <FileText className="size-6" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-72 flex-col p-0"
          side="right"
          sideOffset={8}
        >
          <div className="border-b p-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">
                {t("documents.title", { defaultValue: "Documents" })}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("documents.new", { defaultValue: "New document" })}
                    className="hover:bg-foreground/15 size-7 rounded-full p-0"
                    size="icon"
                    variant="ghost"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleCreateDocument()
                      setIsCollapsedPopoverOpen(false)
                    }}
                  >
                    <Plus className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={5}>
                  <p>{t("documents.new", { defaultValue: "New document" })}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                className="h-8 pl-9 text-sm"
                value={localSearchTerm}
                aria-label={t("documents.search_placeholder", {
                  defaultValue: "Search documents...",
                })}
                placeholder={t("documents.search_placeholder", {
                  defaultValue: "Search documents...",
                })}
                onChange={(event) => setLocalSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape" && localSearchTerm) {
                    setLocalSearchTerm("")
                  }
                }}
              />
            </div>
          </div>
          <div className="relative flex min-h-0 flex-1 flex-col">
            {showAllItems && (
              <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-linear-to-b to-transparent" />
            )}
            <div
              className={cn(
                "overflow-y-auto",
                showAllItems ? "max-h-[300px] pb-10" : "max-h-[300px]"
              )}
            >
              <div className="flex flex-col gap-1 p-1">
                {isLoadingDocuments && (
                  <p className="animate-shimmer from-muted-foreground via-background to-muted-foreground bg-linear-to-r bg-size-[200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
                    {t("documents.loading", { defaultValue: "Loading documents..." })}
                  </p>
                )}
                {!isLoadingDocuments && filteredDocuments.length === 0 && (
                  <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                    {effectiveSearchTerm
                      ? t("documents.no_matches", { defaultValue: "No documents found" })
                      : t("documents.empty", { defaultValue: "No documents yet" })}
                  </p>
                )}
                {!isLoadingDocuments && visibleFavoriteDocuments.length > 0 && (
                  <Collapsible
                    open={isFavoritesSectionOpen}
                    onOpenChange={setIsFavoritesSectionOpen}
                  >
                    <section className="flex flex-col gap-1">
                      <CollapsibleTrigger
                        className="text-muted-foreground/70 dark:text-sidebar-foreground/75 hover:text-muted-foreground dark:hover:text-sidebar-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                        disabled={isSearchingDocuments}
                        type="button"
                      >
                        {t("documents.section_favorites", { defaultValue: "Favorites" })}
                        <ChevronDown
                          className={cn(
                            "text-muted-foreground/70 dark:text-sidebar-foreground/75 group-hover/section:text-muted-foreground dark:group-hover/section:text-sidebar-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                            !isFavoritesSectionOpen && "-rotate-90"
                          )}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="flex flex-col">
                          {visibleFavoriteDocuments.map(renderDocumentItem)}
                        </div>
                      </CollapsibleContent>
                    </section>
                  </Collapsible>
                )}
                {!isLoadingDocuments && visibleRecentDocuments.length > 0 && (
                  <Collapsible
                    open={isSearchingDocuments ? true : isRecentSectionOpen}
                    onOpenChange={setIsRecentSectionOpen}
                  >
                    <section className="flex flex-col gap-1">
                      <CollapsibleTrigger
                        className="text-muted-foreground/70 dark:text-sidebar-foreground/75 hover:text-muted-foreground dark:hover:text-sidebar-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                        disabled={isSearchingDocuments}
                        type="button"
                      >
                        {t("documents.section_recent", { defaultValue: "Recent" })}
                        <ChevronDown
                          className={cn(
                            "text-muted-foreground/70 dark:text-sidebar-foreground/75 group-hover/section:text-muted-foreground dark:group-hover/section:text-sidebar-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                            !isRecentSectionOpen && "-rotate-90"
                          )}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="flex flex-col">
                          {visibleRecentDocuments.map(renderDocumentItem)}
                        </div>
                      </CollapsibleContent>
                    </section>
                  </Collapsible>
                )}
                {hasMoreItems && !effectiveSearchTerm && !showAllItems && (
                  <Button
                    className="text-muted-foreground dark:text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllItems(true)}
                  >
                    {t("sidebar.see_all", { defaultValue: "See all" })} ({filteredDocuments.length})
                  </Button>
                )}
              </div>
            </div>
            {showAllItems && !effectiveSearchTerm && (
              <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                <div className="from-popover h-6 bg-linear-to-t to-transparent" />
                <div className="bg-popover px-1 pb-1">
                  <Button
                    className="text-muted-foreground dark:text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllItems(false)}
                  >
                    {t("sidebar.show_less", { defaultValue: "Show less" })}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <SidebarNavSection
      allItemsShown={showAllItems}
      className={className}
      defaultExpanded={defaultExpanded}
      forceExpanded={!!globalSearchTerm}
      headerActions={headerActions}
      icon={<FileText className="size-4" />}
      isSearchActive={isSearchOpen && !globalSearchTerm}
      searchPlaceholder={t("documents.search_placeholder", { defaultValue: "Search documents..." })}
      searchTerm={localSearchTerm}
      storageKey="sidebar-documents-collapsed"
      title={t("documents.title", { defaultValue: "Documents" })}
      totalCount={filteredDocuments.length}
      visibleCount={sectionVisibleCount}
      onHeaderClick={() => void navigate("/documents")}
      onSearchChange={setLocalSearchTerm}
      onSearchClose={() => setIsSearchOpen(false)}
      onSeeAllClick={() => setShowAllItems(true)}
      onShowLessClick={() => setShowAllItems(false)}
    >
      {isLoadingDocuments && (
        <p className="animate-shimmer from-muted-foreground via-background to-muted-foreground bg-linear-to-r bg-size-[200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
          {t("documents.loading", { defaultValue: "Loading documents..." })}
        </p>
      )}
      {!isLoadingDocuments && filteredDocuments.length === 0 && (
        <p className="text-muted-foreground px-2 py-4 text-center text-xs">
          {effectiveSearchTerm
            ? t("documents.no_matches", { defaultValue: "No documents found" })
            : t("documents.empty", { defaultValue: "No documents yet" })}
        </p>
      )}
      <section className="flex flex-col gap-3">
        {visibleFavoriteDocuments.length > 0 && (
          <Collapsible open={isFavoritesSectionOpen} onOpenChange={setIsFavoritesSectionOpen}>
            <section className="flex flex-col gap-1">
              <CollapsibleTrigger
                className="text-muted-foreground/70 dark:text-sidebar-foreground/75 hover:text-muted-foreground dark:hover:text-sidebar-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                disabled={isSearchingDocuments}
                type="button"
              >
                {t("documents.section_favorites", { defaultValue: "Favorites" })}
                <ChevronDown
                  className={cn(
                    "text-muted-foreground/70 dark:text-sidebar-foreground/75 group-hover/section:text-muted-foreground dark:group-hover/section:text-sidebar-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                    !isFavoritesSectionOpen && "-rotate-90"
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-col">
                  {visibleFavoriteDocuments.map(renderDocumentItem)}
                </div>
              </CollapsibleContent>
            </section>
          </Collapsible>
        )}
        {visibleRecentDocuments.length > 0 && (
          <Collapsible
            open={isSearchingDocuments ? true : isRecentSectionOpen}
            onOpenChange={setIsRecentSectionOpen}
          >
            <section className="flex flex-col gap-1">
              <CollapsibleTrigger
                className="text-muted-foreground/70 dark:text-sidebar-foreground/75 hover:text-muted-foreground dark:hover:text-sidebar-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                disabled={isSearchingDocuments}
                type="button"
              >
                {t("documents.section_recent", { defaultValue: "Recent" })}
                <ChevronDown
                  className={cn(
                    "text-muted-foreground/70 dark:text-sidebar-foreground/75 group-hover/section:text-muted-foreground dark:group-hover/section:text-sidebar-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                    !isRecentSectionOpen && "-rotate-90"
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-col">
                  {visibleRecentDocuments.map(renderDocumentItem)}
                </div>
              </CollapsibleContent>
            </section>
          </Collapsible>
        )}
      </section>
    </SidebarNavSection>
  )
}
